import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Smartphone, Settings, Sparkles } from 'lucide-react';
import Button from '@/components/ui/Button';

interface PricingPack {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  custom?: boolean;
}

interface AdditionalOption {
  icon: React.ReactNode;
  title: string;
  price: string;
  description: string;
  priceInfo?: string;
}

const packs: PricingPack[] = [
  {
    name: "Pack Essentiel",
    price: "7 990 €",
    description: "La solution idéale pour lancer votre marketplace rapidement",
    features: [
      "Site Web compatible desktop & mobile",
      "Publication et vente en ligne",
      "Transactions sécurisées",
      "Intégration d'un service de livraison",
      "Photos de vérification",
      "Étiquettes d'expédition auto.",
      "Dashboard de suivi",
      "Maintenance 3 mois incluse",
      "Commission sur ventes",
      "Optimisation SEO"
    ]
  },
  {
    name: "Pack Premium",
    price: "11 990 €",
    description: "Toutes les fonctionnalités avancées pour une marketplace complète",
    features: [
      "Tout le Pack Essentiel",
      "Vente en lot avec panier",
      "Module de réservation",
      "Messagerie sécurisée",
      "Emails personnalisés",
      "Multi-transporteurs",
      "Boosts et promotions",
      "Maintenance 4 mois incluse"
    ],
    highlighted: true
  },
  {
    name: "Pack Sur Mesure",
    price: "Sur devis",
    description: "Une solution entièrement personnalisée selon vos besoins spécifiques",
    features: [
      "Tout le Pack Premium",
      "Fonctionnalités sur mesure",
      "Design personnalisé",
      "Intégrations spécifiques",
      "Architecture dédiée",
      "Support prioritaire",
      "Formation équipe",
      "Accompagnement stratégique",
      "Maintenance personnalisée"
    ],
    custom: true
  }
];

const additionalOptions: AdditionalOption[] = [
  {
    icon: <Smartphone className="w-6 h-6" />,
    title: "Application mobile",
    price: "5 000 €",
    description: "Version mobile compatible Android & iOS"
  },
  {
    icon: <Smartphone className="w-6 h-6" />,
    title: "Publication Stores",
    price: "790 €",
    description: "Déploiement sur App Store & Play Store"
  },
  {
    icon: <Settings className="w-6 h-6" />,
    title: "Maintenance",
    price: "400 €/mois",
    description: "Support et mises à jour continues"
  }
];

const PricingSection = () => {
  return (
    <section id="packs" className="py-20 lg:py-24 bg-slate-50">
      <div className="container px-4 mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Nos <span className="text-orcsys-500">packs</span> tout inclus
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Choisissez la solution qui correspond le mieux à vos besoins. 
            Tous nos packs incluent un accompagnement personnalisé.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {packs.map((pack, index) => (
            <motion.div
              key={index}
              className={`relative rounded-2xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] hover:shadow-xl ${
                pack.highlighted 
                  ? 'bg-gradient-to-br from-orcsys-500 to-orcsys-600 text-white' 
                  : pack.custom
                  ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-white'
                  : 'bg-white hover:border-orcsys-200'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              {pack.highlighted && (
                <div className="absolute top-0 right-0 bg-white text-orcsys-500 px-4 py-1 rounded-bl-lg text-sm font-medium">
                  Recommandé
                </div>
              )}
              {pack.custom && (
                <div className="absolute top-0 right-0 bg-white text-slate-900 px-4 py-1 rounded-bl-lg text-sm font-medium">
                  Personnalisé
                </div>
              )}

              <div className="p-8">
                <h3 className={`text-2xl font-bold mb-2 ${
                  pack.highlighted || pack.custom ? 'text-white' : 'text-slate-900'
                }`}>
                  {pack.name}
                </h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-4xl font-bold">{pack.price}</span>
                  {!pack.custom && (
                    <span className={`ml-2 ${
                      pack.highlighted ? 'text-white/80' : 'text-slate-500'
                    }`}>HT</span>
                  )}
                </div>
                <p className={`mb-6 ${
                  pack.highlighted || pack.custom ? 'text-white/90' : 'text-slate-600'
                }`}>
                  {pack.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {pack.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className={`w-5 h-5 mr-3 flex-shrink-0 ${
                        pack.highlighted ? 'text-white' : pack.custom ? 'text-white' : 'text-orcsys-500'
                      }`} />
                      <span className={
                        pack.highlighted || pack.custom ? 'text-white/90' : 'text-slate-600'
                      }>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button.Link
                  href="#contact"
                  className={`w-full justify-center group ${
                    pack.highlighted 
                      ? 'bg-white text-orcsys-600 hover:bg-white/90'
                      : pack.custom
                      ? 'bg-white text-slate-900 hover:bg-white/90'
                      : 'bg-orcsys-500 text-white hover:bg-orcsys-600'
                  }`}
                  onClick={() => {
                    const contactSection = document.getElementById('contact');
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                      // Update the form with the selected pack
                      const packSelect = document.getElementById('pack') as HTMLSelectElement;
                      if (packSelect) {
                        packSelect.value = `${pack.name}`;
                        // Trigger change event
                        const event = new Event('change', { bubbles: true });
                        packSelect.dispatchEvent(event);
                      }
                    }
                  }}
                >
                  {pack.custom ? (
                    <span className="flex items-center justify-center">
                      Discuter de votre projet
                      
                    </span>
                  ) : (
                    'Choisir ce pack'
                  )}
                </Button.Link>
              </div>

              {/* Decorative corner sparkle for custom pack */}
              {pack.custom && (
                <div className="absolute top-4 left-4 text-white/20">
                  <Sparkles className="w-6 h-6" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Additional Options */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Options supplémentaires
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {additionalOptions.map((option, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 rounded-lg bg-orcsys-50 flex items-center justify-center mb-4">
                  {option.icon}
                </div>
                <h4 className="text-lg font-semibold text-slate-900 mb-2">
                  {option.title}
                </h4>
                <div className="flex flex-col mb-2">
                  <span className="text-2xl font-bold text-orcsys-500">{option.price}</span>
                  {option.priceInfo && (
                    <div className="mt-1.5">
                    </div>
                  )}
                </div>
                <p className="text-slate-600">{option.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection; 